import { ChangeDetectorRef, Component, OnDestroy, OnInit, Input } from '@angular/core';
import { ShareService } from '@core/services/share-data/share-data.service';
import { ScreenshotService } from '@core/services/screenshot/screenshot.service';
import { OverviewLinkHandlerService } from '@core/services/overview-link-handler/overview-link-handler.service';
import { SetupPageService } from '@core/services/setup-page/setup-page.service';
import { firstValueFrom, combineLatest, Subscription, of } from 'rxjs';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { EngineService } from '@core/services/engine/engine.service';
import { IMAGE_DETECTOR, IV_VIEW_TYPES, STORAGE_NAMES, VIEW_TYPES } from '@root/app.config';
import { BlindData, BlindProperties } from '@root/app.interfaces';

import * as _ from 'lodash';
import { first, delay, filter } from 'rxjs/operators';
import { GtmService } from '@core/services/gtm/gtm.service';
import { GTM_EVENTS } from '@core/services/gtm/gtm.events';
import { PassDataService } from '@core/services/pass-data/pass-data.service';
import { BlindStateService } from '@core/services/blind-state/blind-state.service';
import { VariantService } from '@core/services/variant/variant.service';
import { QuizService } from '@core/services/quiz/quiz.service';

@Component({
	selector: 'app-add-blind',
	templateUrl: './add-blind.component.html',
	styleUrls: ['./add-blind.component.scss', './add-blind.responsive.scss'],
})
export class AddBlindComponent implements OnInit, OnDestroy {
	@Input() isToolBarCollapsed: boolean;

	data: BlindData[] = [];
	blindId: number;
	setBlindInfoStatus = false;
	isModelLoaded: string;
	overviewStatus: boolean;
	blindsConfigured = false;
	isBlockedByBlindsValidity = false;

	getBlindName: Subscription;
	getBlindInfoStatus: Subscription;
	getBlindEmpty: Subscription;
	getModelLoaded: Subscription;
	getBlindNameStatus: Subscription;
	getBlindStatus: Subscription;
	getBlindsConfigured: Subscription;
	getSceneCreated: Subscription;
	getBlindType: Subscription;
	getAddBlindEvent: Subscription;
	getScreenshotCreated: Subscription;
	blindsAvailabilitySubs: Subscription;

	constructor(
		private quizService: QuizService,
		private shareDataService: ShareService,
		private screenShotService: ScreenshotService,
		private overviewLinkHandlerService: OverviewLinkHandlerService,
		private setupPageService: SetupPageService,
		private changeDetection: ChangeDetectorRef,
		private sessionStorageService: SessionStorageService,
		private engineService: EngineService,
		private gtm: GtmService,
		private passDataService: PassDataService,
		private blindStateService: BlindStateService,
		private variant: VariantService,
	) {}

	ngOnInit(): void {
		const blindDataStorage = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);
		this.getModelLoaded = this.shareDataService.getModelLoaded.subscribe((res: boolean) => {
			this.setBlindInfoStatus = res;
			this.changeDetection.markForCheck();
		});
		this.getBlindInfoStatus = this.shareDataService.getStatus.subscribe((res: boolean) => {
			this.setBlindInfoStatus = res;
			this.changeDetection.markForCheck();
		});
		this.getBlindStatus = this.shareDataService.getEmptyBlindData.subscribe((res: boolean) => {
			this.overviewStatus = res;
			this.changeDetection.markForCheck();
		});

		if (this.variant.isShop()) {
			this.getBlindsConfigured = this.shareDataService.getBlindsConfigured.subscribe((res: boolean) => {
				this.blindsConfigured = res;
				this.changeDetection.markForCheck();
			});
		} else {
			this.blindsConfigured = true;
		}

		this.getSceneCreated = this.shareDataService.getSceneCreated.subscribe((res: string) => (this.isModelLoaded = res));
		this.blindsAvailabilitySubs = this.shareDataService.getBlindsValidityStatus.subscribe((blindsValidity) => {
			this.isBlockedByBlindsValidity = Object.values(blindsValidity).some((blindValidity) => blindValidity.block);
		});
		this.getBlindType = this.shareDataService.getBlindType.subscribe(this.onSelectOption.bind(this));

		this.overviewStatus = _.isEmpty(blindDataStorage);
		this.getBlindName = this.shareDataService.getBlindName.subscribe(this.hasBlinds.bind(this));
		this.getBlindEmpty = this.shareDataService.getBlindEmpty.subscribe(this.hasBlinds.bind(this));
		this.getBlindNameStatus = this.shareDataService.getStatus.subscribe(this.blindStatusHandler.bind(this));
		this.getAddBlindEvent = this.shareDataService.getAddBlind.subscribe(this.onAddBlindHandler.bind(this));
	}

	ngOnDestroy(): void {
		this.getModelLoaded.unsubscribe();
		this.getBlindInfoStatus.unsubscribe();
		this.getBlindName.unsubscribe();
		this.getBlindEmpty.unsubscribe();
		this.getBlindNameStatus.unsubscribe();
		this.getBlindType.unsubscribe();
		this.getBlindsConfigured?.unsubscribe();
		this.getSceneCreated.unsubscribe();
		this.getAddBlindEvent.unsubscribe();
		this.getScreenshotCreated?.unsubscribe();
		this.blindsAvailabilitySubs?.unsubscribe();
	}

	hasBlinds(isBlindEmpty: boolean | string): void {
		this.overviewStatus = _.isBoolean(isBlindEmpty) && isBlindEmpty;

		if (this.overviewStatus) {
			this.setupPageService.setWelcomeModal();
		}

		this.changeDetection.markForCheck();
	}

	blindStatusHandler(status: boolean): void {
		const blindDataStorage = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);

		if (!_.isEmpty(blindDataStorage)) {
			this.overviewStatus = !status;
		}

		this.changeDetection.markForCheck();
	}

	onAddBlindHandler(saveScreenShot = true): void {
		const productType = this.setupPageService.getProductType();

		if (productType === 'all') {
			this.setupPageService
				.sendBlindTypeForRender(false, saveScreenShot ? this.shareDataService.getScreenShotBLindTemp : null)
				?.afterOpened()
				.subscribe(() => {
					if (saveScreenShot) {
						this.saveCurrentBlindScreenshot();
					}
				});
		} else {
			if (saveScreenShot && !this.overviewStatus) {
				this.getScreenshotCreated = this.shareDataService.getScreenShotBLindTemp
					.pipe(first(), delay(20))
					.subscribe(this.onScreenshotCreated.bind(this, productType));
				this.saveCurrentBlindScreenshot();
			} else {
				const isNoBackgroundMode = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_iv_type) === IV_VIEW_TYPES.no_background;
				const viewType$ = isNoBackgroundMode ? of(undefined) : this.shareDataService.getViewType;
				combineLatest([this.shareDataService.getModelLoaded, viewType$])
					.pipe(first(), delay(20))
					.subscribe(this.onScreenshotCreated.bind(this, productType));
			}
		}
	}

	private onScreenshotCreated(productType: string): void {
		const id = this.setupPageService.generateUniqueBlindId();
		this.engineService.createScene(productType, id.toString());
		this.sessionStorageService.setSession(productType, STORAGE_NAMES.zip_blind_type);
		void this.onSelectOption({ type: productType, id, buttonPressed: true });
	}

	async onSelectOption(data: BlindProperties): Promise<void> {
		const sessionData = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);
		const defaultBlindName = data.type === 'outdoor' ? 'Ziptrak® Outdoor Blind' : 'Ziptrak® Interior Blind';
		const isAutoCreated = this.passDataService.autoCreatedBlindId === data.id;

		this.passDataService.autoCreatedBlindId = null;
		this.blindId = data.id;

		const defaultBlindTemp = [
			{
				default_name: defaultBlindName,
				blind_id: this.blindId,
				type: data.type,
				meta: sessionData?.meta ? { ...sessionData.meta, is_auto_created: isAutoCreated } : { is_auto_created: isAutoCreated },
			},
		];

		if (_.isEmpty(sessionData)) {
			this.data = defaultBlindTemp;
		} else {
			this.data = sessionData.concat(defaultBlindTemp);
		}

		this.sessionStorageService.setBlindData(this.data, STORAGE_NAMES.zip_blind_data);
		this.sessionStorageService.setBlindData(this.blindId, STORAGE_NAMES.zip_current_blind_id);
		this.sessionStorageService.removeBlindData(STORAGE_NAMES.zip_last_opened_blind_id);
		this.shareDataService.changeData(this.data);
		this.shareDataService.setBlindSelect({ id: this.blindId, status: 'new' });
		this.shareDataService.setScreenShotStatus(this.engineService.isImageVisualisation);

		const isDetection =
			this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_view_type) === VIEW_TYPES.image_visualisation &&
			this.sessionStorageService.getSession(STORAGE_NAMES.zip_iv_type) === 'upload' &&
			this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data).length > 1 &&
			!this.shareDataService.getSelectOpeningModeValue();

		if (IMAGE_DETECTOR === 'off' ? false : isDetection) {
			this.shareDataService.setSelectOpeningMode(true);
			this.quizService.showQuizIfNeeded('add_detection');
			await this.shareDataService.setblindDisplayStatusesRequest();
			this.engineService.toggleAllRootMeshes(false);
		}

		this.blindStateService.blindCreated(this.blindId);

		if (!isDetection && this.variant.isShop() && data?.buttonPressed) {
			await firstValueFrom(
				this.shareDataService.getBboxApplied.pipe(
					filter((id) => +id === this.blindId),
					first(),
				),
			);

			this.blindStateService.openNextSection(this.blindId);
		}
	}

	overviewLinkHandler(): void {
		this.overviewLinkHandlerService.link().then();

		this.gtm.pushEvent(GTM_EVENTS.OVERVIEW_BUTTON);
	}

	saveCurrentBlindScreenshot(): void {
		const currentBlindId = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_current_blind_id);
		const lastOpenedBlindId = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_last_opened_blind_id);
		const hasSessionData = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);

		if (!_.isEmpty(hasSessionData)) {
			this.screenShotService.saveScreenShot(currentBlindId || lastOpenedBlindId, true);
		}
	}
}
